import { useState } from 'react';
import { GraduationCap, BookOpen, Clock, Star, Users, Check, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn, getButtonColor } from '@/lib/utils';
import { useTeacher } from '@/context/TeacherContext';

export default function TeacherTraining() {
  const { teacherTheme, isDark } = useTeacher();

  const trainingModules = [
    {
      id: 1,
      title: 'AI-Powered Assessment Techniques',
      category: 'Assessment',
      duration: '25 min',
      difficulty: 'INTERMEDIATE',
      difficultyColor: 'orange',
      progress: 0,
      status: 'not-started',
      rating: 4.7,
      completed: 1243,
      description: 'Learn how to leverage AI tools for more effective student assessment'
    },
    {
      id: 2,
      title: 'Engaging Students with Interactive Content',
      category: 'Pedagogy',
      duration: '30 min',
      difficulty: 'BEGINNER',
      difficultyColor: 'green',
      progress: 60,
      status: 'in-progress',
      rating: 4.8,
      completed: 2156,
      description: 'Strategies to create more engaging and interactive classroom experiences'
    },
    {
      id: 3,
      title: 'Personalized Learning Pathways',
      category: 'Pedagogy',
      duration: '35 min',
      difficulty: 'ADVANCED',
      difficultyColor: 'red',
      progress: 100,
      status: 'completed',
      rating: 4.6,
      completed: 892,
      description: 'Design customized learning experiences for different student needs'
    },
    {
      id: 4,
      title: 'Digital Classroom Management',
      category: 'Management',
      duration: '20 min',
      difficulty: 'INTERMEDIATE',
      difficultyColor: 'orange',
      progress: 0,
      status: 'not-started',
      rating: 4.5,
      completed: 1567,
      description: 'Best practices for managing digital learning environments'
    }
  ];



  return (
    <div className={cn("flex flex-col h-full p-6 overflow-y-auto", isDark ? "bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--card))] to-[hsl(var(--background))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]")}>
      <div className="max-w-7xl mx-auto w-full space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={cn("text-4xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#1A1A1A]")}>Teacher Training & Development</h1>
            <p className={cn("mt-1 text-base font-medium", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Enhance your teaching skills with AI-powered micro-learning modules</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", isDark ? "bg-[hsl(var(--gold-primary))]/20" : "bg-[#C9A961]/20")}>
                  <BookOpen className={cn("w-6 h-6", isDark ? "text-[hsl(var(--gold-primary))]" : "text-[#C9A961]")} />
                </div>
                <div>
                  <div className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>4</div>
                  <div className={cn("text-sm font-bold uppercase tracking-wide", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Available Modules</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", isDark ? "bg-blue-500/10" : "bg-[#E0D4BF]")}>
                  <Clock className={cn("w-6 h-6", isDark ? "text-blue-400" : "text-[#2B2B2B]")} />
                </div>
                <div>
                  <div className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>2h 30m</div>
                  <div className={cn("text-sm font-bold uppercase tracking-wide", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Total Duration</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Navigation - Only Training Modules Tab */}


        {/* Training Modules Content */}
            <Card className={cn("border shadow-sm", isDark ? "bg-gradient-to-br from-[hsl(var(--card))] to-[hsl(var(--card))] border-[hsl(var(--card-border))]" : "bg-gradient-to-br from-[#F5F1EA] to-[#EDE3D2] border-[#E0D4BF]")}>
              <CardHeader className={cn("border-b py-4", isDark ? "border-[hsl(var(--card-border))] bg-[hsl(var(--card-border))]/10" : "border-[#E0D4BF] bg-[#F0E8D8]/50")}>
                <div className="flex justify-between items-center">
                  <CardTitle className={cn("text-2xl font-bold uppercase", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>Available Training Modules</CardTitle>
                  <select className={cn("px-4 py-2 rounded-lg text-sm focus:ring-2 focus:border-transparent", isDark ? "bg-[hsl(var(--card-border))]/30 border-[hsl(var(--card-border))] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--gold-primary))]" : "bg-white border-[#E0D4BF] text-[#2B2B2B] focus:ring-[#C9A961]")}>
                    <option>All Categories</option>
                    <option>Assessment</option>
                    <option>Classroom Management</option>
                    <option>Technology</option>
                    <option>Pedagogy</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {trainingModules.map((module) => (
                    <div key={module.id} className={cn("rounded-xl border p-4 relative flex flex-col transition-all hover:shadow-md", isDark ? "bg-[hsl(var(--background))]/50 border-[hsl(var(--card-border))] hover:border-[hsl(var(--gold-primary))]" : "bg-white/60 border-[#E0D4BF] hover:border-[#C9A961]")}>
                      <div className={cn("absolute top-3 right-3 w-6 h-6 rounded flex items-center justify-center", isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#C9A961]")}>
                        <GraduationCap className="w-3 h-3 text-white" />
                      </div>

                      <div className="text-center mb-4">
                        <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-3", isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#C9A961]")}>
                          <GraduationCap className="w-6 h-6 text-white" />
                        </div>
                        <h4 className={cn("font-bold text-base mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{module.title}</h4>
                        <div className={cn("flex items-center justify-center space-x-2 text-sm font-medium mb-2", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                          <span className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{module.duration}</span>
                          </span>
                          <span className={cn(
                            "px-2 py-1 text-xs font-medium rounded",
                            isDark
                              ? "bg-[hsl(var(--gold-primary))] text-white"
                              : "bg-[#2B2B2B] text-white"
                          )}>
                            {module.difficulty}
                          </span>
                        </div>
                      </div>

                      <p className={cn("text-sm font-medium mb-3 text-center", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{module.description}</p>

                      <div className={cn("flex items-center justify-between text-sm font-medium mb-3", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>
                        <div className="flex items-center space-x-1">
                          <Star className={cn("w-3 h-3 fill-current", isDark ? "text-[#FBBF24]" : "text-[#F59E0B]")} />
                          <span>{module.rating}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Users className="w-3 h-3" />
                          <span>{module.completed} completed</span>
                        </div>
                      </div>

                      {module.progress > 0 && (
                        <div className="mb-3">
                          <div className={cn("w-full rounded-full h-1.5", isDark ? "bg-[hsl(var(--card-border))]" : "bg-[#E0D4BF]")}>
                            <div
                              className={cn("h-1.5 rounded-full", isDark ? "bg-[hsl(var(--gold-primary))]" : "bg-[#C9A961]")}
                              style={{ width: `${module.progress}%` }}
                            />
                          </div>
                          <p className={cn("text-sm font-medium mt-1", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>{module.progress}% Complete</p>
                        </div>
                      )}

                      <div className="flex-1"></div>

                      <Button
                        className={cn(
                          "w-full text-xs font-medium flex items-center justify-center space-x-1 mt-auto transition-all shadow-md hover:shadow-lg rounded-lg h-9",
                          getButtonColor(isDark)
                        )}
                      >
                        {module.status === 'completed' ? (
                          <>
                            <Check className="w-3 h-3" />
                            <span>Review</span>
                          </>
                        ) : module.status === 'in-progress' ? (
                          <>
                            <Play className="w-3 h-3" />
                            <span>Continue</span>
                          </>
                        ) : (
                          <>
                            <Play className="w-3 h-3" />
                            <span>Start</span>
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

      </div>
    </div>
  );
}
